// CARTAO POR RESERVA E LANCAMENTO (ADR-0014) — helpers da ficha do banco.
//
// A RESERVA trava a margem do balde de cartao e nunca vai no movimento; tem um
// prazo de N parcelas. O LANCAMENTO e' a parcela de UMA competencia, e e' ele
// que a folha desconta. Os dois passam pelo MESMO formulario do emprestimo
// (`AverbarMargemModal`, em ficha.tsx), com as condicoes do contrato que o
// banco digita (05/10/2026). Aqui ficam o select das cinco operacoes, os
// rotulos e o historico de lancamentos.

import {
  OPERACOES_MARGEM,
  ROTULO_OPERACAO_MARGEM,
  baldeDaOperacao,
  operacaoDoCartao,
  operacaoEhLancamento,
  type LancamentoCartao,
  type OperacaoMargem,
  type ReservaCartao,
  type TipoMargem,
} from "@atlas/types";
import { comprometeMargem } from "@atlas/domain";
import { SelectField, fmtBRL } from "@atlas/ui/web";

export const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "grid", placeItems: "center", zIndex: 100, backdropFilter: "blur(6px)",
};
export const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 560, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  // Sem isto o modal crescia alem da viewport e NAO rolava: o form de averbacao
  // (13 campos + anexo + senha do servidor) ficava com o rodape cortado e nao
  // dava pra chegar no botao de salvar. Cliente 05/08/2026.
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
};

export const caixaAviso: React.CSSProperties = {
  padding: 12, borderRadius: 8, fontSize: 13, color: "var(--text)", lineHeight: 1.5,
  background: "color-mix(in srgb, var(--gold-500) 10%, transparent)",
  border: "1px solid color-mix(in srgb, var(--gold-500) 40%, transparent)",
};
export const caixaErro: React.CSSProperties = {
  padding: "8px 12px", borderRadius: 8, fontSize: 13, color: "var(--danger-500)",
  background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)",
};

export interface MargemDoBalde {
  tipo: TipoMargem;
  label: string;
  disponivel: number;
}

/** A reserva ATIVA deste banco no balde — uma por banco, vinculo e produto. */
export function reservaAtivaDoBalde(reservas: readonly ReservaCartao[], balde: TipoMargem): ReservaCartao | undefined {
  return reservas.find((r) => r.produto === balde && comprometeMargem(r.situacao));
}

/**
 * As cinco operacoes do "Tipo de margem", na ordem do pedido do cliente, com o
 * numero que decide cada uma: o disponivel do balde para empréstimo e reserva,
 * a parcela contratada na reserva para o lancamento.
 */
export function opcoesOperacaoMargem(
  margens: readonly MargemDoBalde[],
  reservas: readonly ReservaCartao[],
): { value: OperacaoMargem; label: string }[] {
  return OPERACOES_MARGEM.map((op) => {
    const balde = baldeDaOperacao(op);
    if (operacaoEhLancamento(op)) {
      const r = reservaAtivaDoBalde(reservas, balde);
      const parcela = r && r.parcelasTotal > 0
        ? r.parcelaVigente != null ? ` · parcela ${r.parcelaVigente} de ${r.parcelasTotal}` : " · parcelas esgotadas"
        : "";
      return { value: op, label: `${ROTULO_OPERACAO_MARGEM[op]} — ${r ? `parcela até ${fmtBRL(r.valorParcela)}${parcela}` : "sem reserva"}` };
    }
    const m = margens.find((x) => x.tipo === balde);
    return { value: op, label: `${ROTULO_OPERACAO_MARGEM[op]} — disp. ${fmtBRL(m?.disponivel ?? 0)}` };
  });
}

/**
 * A operacao que o card de um balde abre: no cartao, RESERVA quando este banco
 * ainda nao reservou, LANCAMENTO quando ja' reservou — e' o proximo passo dele.
 */
export function operacaoDoCard(balde: TipoMargem, reservas: readonly ReservaCartao[]): OperacaoMargem {
  if (balde === "EMPRESTIMO") return "EMPRESTIMO";
  return operacaoDoCartao(reservaAtivaDoBalde(reservas, balde) ? "lancamento" : "reserva", balde);
}

/** `202610` -> `10/2026`. */
export function rotuloCompetencia(c: string): string {
  return /^\d{6}$/.test(c) ? `${c.slice(4, 6)}/${c.slice(0, 4)}` : c;
}

/** Instante do corte, na hora de Brasilia: `15/10 17:00`. */
export function rotuloCorte(iso: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso ?? "");
  return m ? `${m[2]}/${m[1]} ${m[3]}:${m[4]}` : "—";
}

const ROTULO_RESULTADO: Record<string, string> = {
  aplicada: "Descontado",
  parcial: "Descontado em parte",
  falha: "Não descontado",
  suspensa_sem_folha: "Sem folha no mês",
  suspensa_por_teto: "Suspenso pelo teto global",
  interrompida_desligamento: "Interrompido (desligamento)",
};

/** O select "Tipo de margem" — o mesmo nos dois formularios da ficha. */
export function SelectOperacaoMargem({
  value,
  margens,
  reservas,
  onChange,
}: {
  value: OperacaoMargem;
  margens: readonly MargemDoBalde[];
  reservas: readonly ReservaCartao[];
  onChange: (op: OperacaoMargem) => void;
}) {
  return (
    <SelectField
      label="Tipo de margem"
      value={value}
      onChange={(e) => onChange(e.target.value as OperacaoMargem)}
      options={opcoesOperacaoMargem(margens, reservas)}
    />
  );
}

/** Os lancamentos de uma reserva, com a parcela de cada um e o que a folha fez. */
export function HistoricoLancamentos({
  lancamentos,
  parcelasTotal,
  cancelando,
  onCancelar,
}: {
  lancamentos: readonly LancamentoCartao[];
  /** Prazo da reserva. 0 = sem prazo. */
  parcelasTotal: number;
  cancelando: boolean;
  onCancelar: (competencia: string) => void;
}) {
  const th: React.CSSProperties = { textAlign: "left", padding: "6px 8px", fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em" };
  const td: React.CSSProperties = { padding: "6px 8px", fontSize: 13, borderTop: "1px solid var(--border)" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
        Lançamentos desta reserva
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Parcela</th>
              <th style={th}>Competência</th>
              <th style={th}>Valor</th>
              <th style={th}>Situação</th>
              <th style={th}>Folha</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {[...lancamentos].reverse().map((l) => (
              <tr key={l.competencia}>
                <td style={td}>{l.parcelaNumero != null ? `${l.parcelaNumero}${parcelasTotal > 0 ? ` de ${parcelasTotal}` : ""}` : "—"}</td>
                <td style={td}>{rotuloCompetencia(l.competencia)}</td>
                <td style={td}>{fmtBRL(l.valor)}</td>
                <td style={td}>
                  {l.estado === "cancelado" ? "Cancelado" : l.congelado ? "Enviado à folha" : "Aguardando o corte"}
                </td>
                <td style={td}>
                  {l.resultado ? `${ROTULO_RESULTADO[l.resultado] ?? l.resultado}${l.valorDescontado != null ? ` (${fmtBRL(l.valorDescontado)})` : ""}` : "—"}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  {l.estado === "informado" && !l.congelado ? (
                    <button
                      type="button"
                      disabled={cancelando}
                      onClick={() => onCancelar(l.competencia)}
                      style={{ padding: "3px 10px", borderRadius: 6, border: "1px solid var(--danger-500)", background: "transparent", color: "var(--danger-500)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >
                      Cancelar
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
